import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '../context/user';
import { YbugApi, YbugContext, YbugContextType, YbugProviderProps } from '../context/ybug';
import { getLanguageCode } from '../languageCodeFormater';

export const YbugProvider = ({ children, settings }: YbugProviderProps) => {
  const YBUG_DEFAULT_SCRIPT_URL = `https://widget.ybug.io/button/${
    import.meta.env.VITE_YBUG_PROJECT_ID
  }.js`;

  const [ybugApi, setYbugApi] = useState<YbugApi | null>(null);
  const { user } = useUser();
  const { i18n } = useTranslation();
  const applicationLanguage = getLanguageCode(i18n.language);

  useEffect(() => {
    const initializeYbug = () => {
      const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email;

      // @ts-expect-error will not be defined in window
      window.ybug_settings = {
        ...settings,
        feedback: {
          email: user?.email,
          name,
        },
        language_override: applicationLanguage,
        id: import.meta.env.VITE_YBUG_PROJECT_ID,
        onload: () => {
          // @ts-expect-error will not be defined in window
          setYbugApi(window.Ybug);
          settings?.onload?.();
        },
      };

      const script = document.createElement('script');
      script.defer = true;
      script.src = YBUG_DEFAULT_SCRIPT_URL;

      if (settings?.nonce) {
        script.nonce = settings.nonce;
      }

      document.head.appendChild(script);

      return script;
    };

    const script = initializeYbug();

    return () => {
      if (ybugApi) {
        ybugApi.destroy();
      }
      script.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ybugApi) {
      if (settings?.hide_launcher) {
        ybugApi.hide();
      } else {
        ybugApi.show();
      }
    }
  }, [ybugApi, settings]);

  const initYbug = useCallback<YbugContextType['init']>(
    (newSettings) => {
      // @ts-expect-error will not be defined in window
      window.ybug_settings = {
        ...newSettings,
        id: import.meta.env.VITE_YBUG_PROJECT_ID,
        onload: () => {
          // @ts-expect-error will not be defined in window
          setYbugApi(window.Ybug);
          newSettings?.onload?.();
        },
      };

      if (ybugApi) {
        ybugApi.destroy();
        ybugApi.boot();
      }
    },
    [ybugApi]
  );

  const contextValue = useMemo(() => ({ Ybug: ybugApi, init: initYbug }), [initYbug, ybugApi]);

  return <YbugContext.Provider value={contextValue}>{children}</YbugContext.Provider>;
};
